/**
 * Single source of truth for the role → permission matrix.
 *
 * Mirrored in `functions/src/orgs/permissions.ts`. The
 * `permission-matrix.spec.ts` test snapshots both into JSON and asserts
 * they match — change one, change the other, run the test.
 *
 * Firestore rules also encode (a coarser version of) this matrix. The
 * rules version doesn't iterate over a permission set; it expresses
 * role-based booleans (`isAdminOf`, `canManageBilling`, …) that map to
 * the bullet points below. The same test asserts those helpers cover
 * every distinguishing permission.
 */

import type { OrgPermission, OrgRole } from './orgs.types';

const VIEWER: readonly OrgPermission[] = [
  'recordings:read',
  'nodes:read',
  'testcases:read',
  'runs:read',
  'reports:read',
  'members:read',
  'departments:read',
  'vms:read',
];

// `nodes:approve` sits at ADMIN tier since 2026-09-17 (meeting feedback #195):
// members author and submit change sets, Admin/Owner approve, reject and
// close. Members can no longer approve their own sets.
const MEMBER: readonly OrgPermission[] = [
  ...VIEWER,
  'recordings:write',
  'nodes:write',
  'testcases:write',
  'runs:execute',
  'secrets:read',
  'vms:provision',
];

const ADMIN: readonly OrgPermission[] = [
  ...MEMBER,
  'members:invite',
  'members:manage',
  'nodes:approve',
  'departments:manage',
  'apiKeys:read',
  'apiKeys:manage',
  'auditLog:read',
  'billing:read',
  'secrets:manage',
  'org:manage',
  'vms:manage_profiles',
];

/** Owner adds `billing:manage`, `billing:portal`, `vms:teardown_org`; ownership transfer is a separate callable check. */
const OWNER: readonly OrgPermission[] = [
  ...ADMIN,
  'billing:manage',
  'billing:portal',
  'vms:teardown_org',
];

export const ROLE_PERMISSIONS: Readonly<Record<OrgRole, readonly OrgPermission[]>> = {
  viewer: VIEWER,
  member: MEMBER,
  admin: ADMIN,
  owner: OWNER,
};

export function hasPermission(role: OrgRole | undefined, perm: OrgPermission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(perm);
}

/** Stable JSON snapshot used by the matrix sync test. */
export function snapshotMatrix(): Record<OrgRole, readonly OrgPermission[]> {
  return {
    owner: [...ROLE_PERMISSIONS.owner].sort(),
    admin: [...ROLE_PERMISSIONS.admin].sort(),
    member: [...ROLE_PERMISSIONS.member].sort(),
    viewer: [...ROLE_PERMISSIONS.viewer].sort(),
  };
}
